import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, CATEGORY, errMsg, fileUrl, fmtDate, STATUS } from "@/lib/api";
import { PriorityBadge, Spinner, StatusBadge } from "@/components/bits";
import TicketChat from "@/components/TicketChat";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, History } from "lucide-react";
import { toast } from "sonner";

export default function ClientTicketDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get(`/tickets/${id}`),
      api.get(`/tickets/${id}/messages`),
      api.get(`/tickets/${id}/history`),
    ]).then(([{ data: t }, { data: m }, { data: h }]) => {
      setTicket(t);
      setMessages(m);
      setHistory(h);
    }).catch((e) => toast.error(errMsg(e)));
  }, [id]);

  if (!ticket) return <Spinner />;

  return (
    <div data-testid="client-ticket-detail">
      <Link to="/portal" className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200 mb-4 transition-colors duration-200">
        <ArrowLeft className="h-3.5 w-3.5" /> Voltar para meus chamados
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <p className="font-mono text-xs text-zinc-500 mb-1">#{String(ticket.number).padStart(4, "0")} · {CATEGORY[ticket.category]}{ticket.area_name && <span className="font-sans" data-testid="ticket-area"> · Local: <span className="text-zinc-300">{ticket.area_name}</span></span>}</p>
          <h1 className="text-2xl tracking-tight font-semibold text-zinc-100" data-testid="ticket-title">{ticket.title}</h1>
          <p className="text-sm text-zinc-400 mt-1">Aberto em {fmtDate(ticket.created_at)}{ticket.assigned_agent_name ? ` · Atendente: ${ticket.assigned_agent_name}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <PriorityBadge value={ticket.priority} />
          <StatusBadge value={ticket.status} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
            <h2 className="text-sm font-medium text-zinc-100 mb-3">Descrição</h2>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
            {ticket.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-4">
                {ticket.attachments.map((p) => (
                  <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" data-testid="ticket-attachment">
                    <img src={fileUrl(p)} alt="anexo" className="h-20 w-20 object-cover rounded-md border border-zinc-800" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
            <h2 className="text-sm font-medium text-zinc-100 mb-4">Conversa com o suporte</h2>
            <TicketChat ticketId={id} messages={messages} currentUser={user}
              onNew={(m) => setMessages([...messages, m])} />
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 h-fit">
          <h2 className="text-sm font-medium text-zinc-100 mb-4 flex items-center gap-2"><History className="h-4 w-4 text-indigo-400" /> Acompanhamento</h2>
          <div className="relative pl-5 space-y-5 before:absolute before:left-[5px] before:top-1 before:bottom-1 before:w-px before:bg-zinc-800" data-testid="ticket-timeline">
            {history.map((h) => (
              <div key={h.id} className="relative">
                <span className="absolute -left-5 top-1 h-2.5 w-2.5 rounded-full bg-indigo-500 ring-4 ring-indigo-500/10" />
                <p className="text-xs text-zinc-300">
                  {h.previous_status ? <>{STATUS[h.previous_status]} → <span className="text-zinc-100 font-medium">{STATUS[h.new_status]}</span></> : <>Chamado aberto</>}
                </p>
                <p className="text-[11px] text-zinc-500 mt-0.5">{fmtDate(h.created_at)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
